import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { populateDatabase } from '../data/populate';
import type { AppRepositories } from '../domain';
import { db } from './database';
import { localRepositories } from './repositories';
import { CAMPUS_DATASET_VERSION } from '../data/profile';

const RepositoryContext = createContext<AppRepositories | null>(null);
let repositoryPreparation: Promise<void> | null = null;

function prepareRepository(): Promise<void> {
  if (!repositoryPreparation) {
    repositoryPreparation = (async () => {
      await db.open();
      const metadata = await db.datasetMetadata.get('canonical');
      if (metadata?.version !== CAMPUS_DATASET_VERSION) {
        await populateDatabase(db);
      }
    })();
  }
  return repositoryPreparation;
}

export function RepositoryProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<'loading' | 'ready' | 'error'>('loading');

  useEffect(() => {
    let active = true;
    async function prepare() {
      try {
        await prepareRepository();
        if (active) setState('ready');
      } catch (error) {
        console.error('Unable to prepare the local CROC-OPS dataset.', error);
        if (active) setState('error');
      }
    }
    void prepare();
    return () => {
      active = false;
    };
  }, []);

  if (state === 'loading') {
    return (
      <main className="app-boot" aria-live="polite">
        <img src="/brand/icon-192.png" alt="" />
        <span>CROC-OPS</span>
        <strong>Preparing the local operations environment</strong>
        <i aria-hidden="true" />
      </main>
    );
  }

  if (state === 'error') {
    return (
      <main className="app-boot app-boot--error" role="alert">
        <img src="/brand/icon-192.png" alt="" />
        <span>CROC-OPS</span>
        <strong>The local dataset could not be prepared.</strong>
        <p>Reload the application to try again. No external system was affected.</p>
      </main>
    );
  }

  return (
    <RepositoryContext.Provider value={localRepositories}>
      {children}
    </RepositoryContext.Provider>
  );
}

export function useRepositories(): AppRepositories {
  const ctx = useContext(RepositoryContext);
  if (!ctx) {
    throw new Error('useRepositories must be used within a RepositoryProvider');
  }
  return ctx;
}
